import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr } from "@/i18n/copy";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { ArtistReviewList } from "@/components/ArtistReviews";

export default function ClientArtistReviews({ relationshipId, artist, initialReviews = [], reviewsUrl }) {
  useUiLanguage();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(initialReviews);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nextSkip, setNextSkip] = useState(0);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState(false);
  const count = Number(artist?.reviews?.count || 0);
  const url = reviewsUrl || `/client/relationships/${relationshipId}/artists/${artist.id}/reviews`;

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKeyDown);
    setLoading(true);
    api.get(`${url}?skip=0`).then((page) => {
      if (!active) return;
      setRows(page.reviews || []);
      setHasMore(Boolean(page.has_more));
      setNextSkip((page.reviews || []).length);
      setError(false);
    }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, url, reload]);

  const loadMore = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const page = await api.get(`${url}?skip=${nextSkip}`);
      setRows((current) => [...current, ...(page.reviews || []).filter((item) => !current.some((row) => row.id === item.id))]);
      setHasMore(Boolean(page.has_more));
      setNextSkip((current) => current + (page.reviews || []).length);
      setError(false);
    } catch { setError(true); }
    finally { setLoading(false); }
  };

  return <>
    <section aria-label={tr("Recensioni di {{0}}", {"0": artist.name})} className="rounded-3xl border border-white/10 bg-card/85 p-6">
      <h2 className="mb-3 font-display text-lg font-bold">{tr("Recensioni")}</h2>
      <ArtistReviewList reviews={initialReviews.slice(0, 5)} />
      {count > 5 && <button type="button" onClick={() => setOpen(true)} className="mt-4 min-h-11 rounded-xl border border-white/20 px-4 text-sm font-semibold hover:bg-white/10">{tr("Vedi tutte le {{count}} recensioni", {count})}</button>}
    </section>
    {open && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-3 backdrop-blur-sm" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}>
      <section role="dialog" aria-modal="true" aria-label={tr("Tutte le recensioni di {{0}}", {"0": artist.name})} className="flex max-h-[90dvh] w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-white/20 bg-zinc-950 text-white shadow-2xl">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 p-4"><div><h2 className="font-display text-xl font-bold">{tr("Recensioni di")} {artist.name}</h2><p className="text-xs text-zinc-400">{count} {tr("valutazioni")}</p></div><button type="button" onClick={() => setOpen(false)} aria-label={tr("Chiudi recensioni")} className="grid h-11 w-11 shrink-0 place-items-center rounded-full hover:bg-white/10"><X className="h-5 w-5" /></button></header>
        <div className="overflow-y-auto p-4"><ArtistReviewList reviews={rows} />{error && <p role="alert" className="mt-3 text-sm">{tr("Impossibile caricare le recensioni. Riprova.")}</p>}{hasMore && <button type="button" disabled={loading} onClick={loadMore} className="mt-4 min-h-11 w-full rounded-xl border border-white/20 px-4 text-sm font-semibold disabled:opacity-50">{loading ? tr("Caricamento…") : tr("Mostra altre recensioni")}</button>}{loading && !hasMore && <p className="mt-3 text-sm text-zinc-400">{tr("Caricamento…")}</p>}{error && !hasMore && <button type="button" onClick={() => setReload((value) => value + 1)} className="mt-3 min-h-11 rounded-xl border px-4 text-sm">{tr("Riprova")}</button>}</div>
      </section>
    </div>}
  </>;
}

